import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@packages/utils';
import { BookingField, TicketType } from '@packages/types';
import { useEvent } from '@/layouts/eventContext';
import { formatEventDateTime } from '@/features/events/eventFormatting';
import { fieldValue, TicketItem, useTicketOrder } from '@/features/ticketing/useTicketOrder';
import { formatEur } from '@/features/ticketing/format';

const Stepper = ({ count, onChange }: { count: number; onChange: (n: number) => void }) => {
  const btn =
    'flex h-9 w-9 min-w-9 max-w-9 basis-9 flex-none appearance-none items-center justify-center p-0 leading-none';
  // Inline SVG glyphs keep the + and - in identical 36px buttons and avoid
  // platform font rendering differences that made the + look oversized.
  const icon = {
    className: 'block h-3.5 w-3.5',
    viewBox: '0 0 14 14',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
  };
  return (
    <div
      className="inline-flex w-auto max-w-max flex-none shrink-0 items-center"
      style={{ border: '1px solid var(--ev-line)' }}>
      <button
        type="button"
        className={btn}
        style={{ color: 'var(--ev-ink)', flex: '0 0 36px', width: 36, minWidth: 36, maxWidth: 36 }}
        aria-label="-"
        onClick={() => onChange(Math.max(0, count - 1))}>
        <svg {...icon} aria-hidden="true">
          <line x1="3.25" y1="7" x2="10.75" y2="7" />
        </svg>
      </button>
      <span
        className="flex h-9 w-9 items-center justify-center font-[var(--ev-mono)] text-base font-bold"
        style={{
          color: 'var(--ev-ink)',
          borderLeft: '1px solid var(--ev-line)',
          borderRight: '1px solid var(--ev-line)',
        }}>
        {count}
      </span>
      <button
        type="button"
        className={btn}
        style={{ color: 'var(--ev-ink)', flex: '0 0 36px', width: 36, minWidth: 36, maxWidth: 36 }}
        aria-label="+"
        onClick={() => onChange(count + 1)}>
        <svg {...icon} aria-hidden="true">
          <line x1="3.25" y1="7" x2="10.75" y2="7" />
          <line x1="7" y1="3.25" x2="7" y2="10.75" />
        </svg>
      </button>
    </div>
  );
};

const EvTicketType = ({
  type,
  count,
  onChange,
}: {
  type: TicketType;
  count: number;
  onChange: (n: number) => void;
}) => {
  const { i18n } = useTranslation();
  return (
    <div
      className="ev-panel p-5"
      style={
        count
          ? {
              borderColor: 'var(--ev-accent)',
              background: 'color-mix(in srgb, var(--ev-accent) 12%, var(--ev-surface))',
            }
          : undefined
      }>
      <div className="flex items-start justify-between gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="ev-disp max-w-full break-words" style={{ fontSize: 24 }}>
            {type.label}
          </div>
          {type.description ? (
            <p
              className="mt-1.5 max-w-[320px] text-sm leading-snug"
              style={{ color: 'var(--ev-muted)' }}>
              {type.description}
            </p>
          ) : null}
        </div>
        <div className="ev-disp ev-accent-text max-w-full shrink-0" style={{ fontSize: 32 }}>
          {formatEur(type.price, i18n.language)}
        </div>
      </div>
      <div className="mt-4">
        <Stepper count={count} onChange={onChange} />
      </div>
    </div>
  );
};

const EvCustomField = ({
  field,
  value,
  onChange,
  missing,
}: {
  field: BookingField;
  value: string;
  onChange: (v: string) => void;
  missing?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <label className="mt-3 block">
      <span className="ev-label">
        {field.label}
        {field.required ? '' : ` (${t('optional')})`}
      </span>
      {field.type === 'textarea' ? (
        <textarea
          className={cn('ev-input', missing && 'ev-input--missing')}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className={cn('ev-input', missing && 'ev-input--missing')}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
};

const EvAttendeeCard = ({
  index,
  item,
  label,
  price,
  incomplete,
  attendeeFields,
  onUpdate,
  onDelete,
}: {
  index: number;
  item: TicketItem;
  label: string;
  price: number;
  incomplete: boolean;
  attendeeFields: BookingField[];
  onUpdate: (t: TicketItem) => void;
  onDelete: () => void;
}) => {
  const { t, i18n } = useTranslation();
  return (
    <div className="ev-glass mb-3.5 p-5">
      <div className="mb-3 flex items-center justify-between">
        <span
          className="ev-mono text-[13px] font-bold uppercase tracking-[0.12em]"
          style={{
            color: 'var(--ev-accent-readable)',
            textShadow: '0.08em 0.08em 0 var(--ev-bg)',
          }}>
          {label} · {formatEur(price, i18n.language)} {incomplete ? `· ${t('data_missing')}` : ''}
        </span>
        <button
          type="button"
          aria-label={t('cancel')}
          onClick={onDelete}
          className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-[13px]"
          style={{ background: 'var(--ev-bg)', color: 'var(--ev-ink)' }}>
          ✕
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <span className="ev-label">
            {t('name')} {index + 1}
          </span>
          <input
            className={cn('ev-input', incomplete && !item.name.trim() && 'ev-input--missing')}
            required
            placeholder="Vorname Nachname"
            value={item.name}
            onChange={(e) => onUpdate({ ...item, name: e.target.value })}
          />
        </div>
        <div>
          <span className="ev-label">{t('email')}</span>
          <input
            className={cn('ev-input', incomplete && !item.email.trim() && 'ev-input--missing')}
            type="email"
            required
            placeholder="name@mail.de"
            value={item.email}
            onChange={(e) => onUpdate({ ...item, email: e.target.value })}
          />
        </div>
      </div>
      {attendeeFields.map((f) => (
        <EvCustomField
          key={f.key}
          field={f}
          value={fieldValue(item.customFields, f)}
          missing={incomplete && f.required && !fieldValue(item.customFields, f).trim()}
          onChange={(v) =>
            onUpdate({ ...item, customFields: { ...item.customFields, [f.key]: v } })
          }
        />
      ))}
    </div>
  );
};

export const EventTicketsPage = () => {
  const { t, i18n } = useTranslation();
  const event = useEvent();
  const date = formatEventDateTime(
    event.startsAt,
    event.endsAt,
    event.startTimeLabel,
    event.endTimeLabel,
  );
  const eur = (n: number) => formatEur(n, i18n.language);

  const order = useTicketOrder({
    ownerInput: { kind: 'event', eventId: event.eventId },
    ownerRef: { kind: 'event', eventId: event.eventId },
    title: 'Tickets',
    saleState: event.saleState,
    bookingFields: event.bookingFields ?? [],
    serviceFeePercent: event.settings?.serviceFeePercent,
    orderPath: (id) => `/events/${event.slug}/orders/${id}`,
    syncBuyerEmailWithFirstTicket: true,
  });

  if (event.saleState !== 'open') {
    return (
      <div className="relative z-[2] px-5 py-12 sm:px-12">
        <div className="ev-glass max-w-[520px] p-7">
          <div className="ev-disp" style={{ fontSize: 32 }}>
            {event.saleState === 'soldOut' ? t('sold_out_title') : t('presale_not_open_title')}
          </div>
          <p className="ev-mono mt-2 text-sm" style={{ color: 'var(--ev-muted)' }}>
            {t('no_tickets_available_event')}
          </p>
          <Link to={`/events/${event.slug}`} className="ev-btn ev-btn--ghost mt-5">
            ← {t('back')}
          </Link>
        </div>
      </div>
    );
  }

  if (order.isLoading) return null;

  const showBuyerEmailField = order.tickets.length !== 1;
  const showOrderDetails = showBuyerEmailField || order.orderFields.length > 0;
  const meta = [date, event.locationName].filter(Boolean).join(' · ');

  const totalCard = (
    <div className="ev-glass p-6">
      <div className="ev-eyebrow">{t('summary')}</div>
      {order.summary.lineItems.length ? (
        <div className="mt-3 flex flex-col gap-2 font-[var(--ev-mono)] text-sm">
          {order.summary.lineItems.map((li) => (
            <div
              key={li.ticketTypeId}
              className="flex items-baseline justify-between gap-3"
              style={{ color: 'var(--ev-ink)' }}>
              <span style={{ color: 'var(--ev-muted)' }}>
                {li.count} × {li.label}
              </span>
              <span>{eur(li.lineTotal)}</span>
            </div>
          ))}
          {order.summary.serviceFee > 0 ? (
            <div
              className="flex items-baseline justify-between gap-3"
              style={{ color: 'var(--ev-ink)' }}>
              <span style={{ color: 'var(--ev-muted)' }}>
                {t('service_fee', { percent: order.summary.serviceFeePercent })}
              </span>
              <span>{eur(order.summary.serviceFee)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="ev-eyebrow mt-4">{t('grand_total')}</div>
      <div className="ev-disp ev-accent-text" style={{ fontSize: 48, lineHeight: 1, marginTop: 4 }}>
        {eur(order.summary.grandTotal)}
      </div>
      <button
        type="button"
        onClick={() => order.onSubmit('ticketForm')}
        disabled={!order.tickets.length || order.isPending}
        className="ev-btn ev-btn--block ev-btn--lg mt-4">
        {t('pay')} →
      </button>
      {order.error ? (
        <p className="ev-mono mt-2 text-sm" style={{ color: 'var(--ev-accent2-readable)' }}>
          {order.error}
        </p>
      ) : null}
    </div>
  );

  const content = (
    <div>
      <h1 className="ev-disp" style={{ fontSize: 'clamp(44px, 7vw, 72px)', marginBottom: 18 }}>
        {t('tickets')}
      </h1>
      <div className="mb-8 max-w-[860px]">
        <h2 className="ev-disp" style={{ fontSize: 'clamp(28px, 4vw, 42px)', lineHeight: 1.05 }}>
          {event.title}
        </h2>
        {meta ? <div className="ev-eyebrow mt-3 break-words leading-relaxed">{meta}</div> : null}
      </div>

      <div className="mb-8 flex flex-col gap-3.5">
        {order.ticketTypes.map((type) => (
          <EvTicketType
            key={type.ticketTypeId ?? type.name}
            type={type}
            count={order.countForType(type)}
            onChange={(n) => order.setTicketCount(type, n)}
          />
        ))}
        {!order.ticketTypes.length ? (
          <p className="ev-mono text-sm" style={{ color: 'var(--ev-muted)' }}>
            {t('no_tickets_for_sale')}
          </p>
        ) : null}
      </div>

      <div ref={order.ticketsRef}>
        {order.tickets.length ? (
          <>
            <hr className="ev-rule mb-5" />
            <h2 className="ev-disp mb-4" style={{ fontSize: 26 }}>
              {t('your_tickets')} ({order.tickets.length})
            </h2>
          </>
        ) : null}
        {order.ticketsWithType.map((line, index) => (
          <EvAttendeeCard
            key={line.ticket.rowId}
            index={index}
            item={line.ticket}
            label={line.ticketType?.label ?? `${t('ticket')} ${index + 1}`}
            price={line.ticketType?.price ?? 0}
            incomplete={order.attempted && order.ticketIncomplete(line.ticket)}
            attendeeFields={order.attendeeFields}
            onUpdate={order.updateTicket}
            onDelete={() => order.deleteTicket(line.ticket)}
          />
        ))}
      </div>

      {showOrderDetails ? (
        <>
          <hr className="ev-rule my-6" />
          {showBuyerEmailField ? (
            <label className="block max-w-[360px]">
              <span className="ev-label">{t('buyer_email')}</span>
              <input
                className="ev-input"
                type="email"
                required
                placeholder="name@mail.de"
                value={order.buyerEmail}
                onChange={(e) => order.setBuyerEmail(e.target.value)}
              />
            </label>
          ) : null}
          {order.orderFields.map((f) => (
            <EvCustomField
              key={f.key}
              field={f}
              value={fieldValue(order.buyerFields, f)}
              missing={order.attempted && f.required && !fieldValue(order.buyerFields, f).trim()}
              onChange={(v) => order.setBuyerFields((prev) => ({ ...prev, [f.key]: v }))}
            />
          ))}
        </>
      ) : null}
    </div>
  );

  return (
    <form
      id="ticketForm"
      className="relative z-[2] grid grid-cols-1 items-start gap-11 px-5 py-11 sm:px-12 lg:grid-cols-[1.25fr_0.9fr]">
      {content}
      {/* Right rail (2-col on desktop): just the order summary. The event poster
          lives on the landing page only, so the checkout step stays focused. */}
      <aside className="lg:sticky lg:top-5">{totalCard}</aside>
    </form>
  );
};
